// Turns a picked image into the inline data URL the org logo is stored as.
// Raster images are shrunk to fit 512px and re-encoded; SVGs pass through.
// Mirrors the limits boo-backend enforces on PATCH /api/org.

const MAX_EDGE = 512;
const MAX_CHARS = 400_000;
const MAX_SVG_BYTES = 300_000;

export const LOGO_ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

export async function fileToLogoDataUrl(file: File): Promise<string> {
  if (file.type === "image/svg+xml") {
    if (file.size > MAX_SVG_BYTES) throw new Error("An SVG logo must be under 300 KB.");
    const bytes = new Uint8Array(await file.arrayBuffer());
    let binary = "";
    for (const b of bytes) binary += String.fromCharCode(b);
    return `data:image/svg+xml;base64,${btoa(binary)}`;
  }
  if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
    throw new Error("Use a PNG, JPEG, WebP or SVG image.");
  }

  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Could not read the image.");
    ctx.drawImage(bitmap, 0, 0, width, height);

    const png = canvas.toDataURL("image/png");
    if (png.length <= MAX_CHARS) return png;
    const webp = canvas.toDataURL("image/webp", 0.85);
    if (webp.startsWith("data:image/webp") && webp.length <= MAX_CHARS) return webp;
    throw new Error("That image is too detailed to store. Try a simpler or smaller logo.");
  } finally {
    bitmap.close();
  }
}
